/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';
import { classNames } from 'primereact/utils';
import { MenuItem } from 'primereact/menuitem';
import { AppRoutes } from '@/constants/routes.ts';
import { Menu } from 'primereact/menu';
import styled from 'styled-components';
import 'rehype-callouts/theme/obsidian';
import { useStoreContext } from '@/contexts/StoreContext';

export interface DocManifestType {
  title: string;
  slug: string;
  order: number;
  rank?: number;
  feature?: string;
  keywords?: string[];
}

interface Props {
  className?: string;
  manifest: DocManifestType[];
  resetSearchResult: () => void;
}

const DocLink = styled(NavLink)`
  text-decoration: none;
  color: var(--text-color);
  display: inline-block;
  padding: 0.4rem 0.5rem;
  width: 100%;
  border-radius: 0;
  margin-bottom: 0 !important;
  &:hover {
    background-color: var(--gray-200);
    transition: all linear 0.1s;
  }
  &.active {
    border-radius: 5px;
    border: none;
  }
`;

const CMenu = styled(Menu)`
  background-color: var(--surface-card);
  border: none;
  .p-submenu-header {
    background: transparent;
  }
`;

type GroupedDocs = Record<string, { title: string; order: number; items: DocManifestType[] }>;

const groupDocsBySlug = (docs: DocManifestType[]): GroupedDocs =>
  docs.reduce((acc, doc) => {
    const groupKey = doc.slug.split('/')[0];
    if (!acc[groupKey]) {
      acc[groupKey] = {
        title: groupKey,
        order: doc.order,
        items: []
      };
    }
    acc[groupKey].items.push(doc);

    return acc;
  }, {} as GroupedDocs);

export const DocSideBar: React.FC<Props> = ({ className, manifest, resetSearchResult }) => {
  const { pathname } = useLocation();
  const { externalServiceConfig } = useStoreContext();

  const docMenuItem: MenuItem[] = useMemo(() => {
    const manifestGrouped = groupDocsBySlug(manifest);

    const manifestGroupedFilered: GroupedDocs = {};
    Object.entries(manifestGrouped).forEach(([key, value]) => {
      const itemsFiltered = value.items.filter((item) => {
        return externalServiceConfig?.spaces?.serviceUp || item.feature !== 'spaces';
      });
      if (itemsFiltered.length > 0) {
        manifestGroupedFilered[key] = {
          ...value,
          items: itemsFiltered
        };
      }
    });

    return Object.entries(manifestGroupedFilered)
      .toSorted(([, a], [, b]) => a.order - b.order)
      .map(([section, v]) => {
        if (section === 'index') {
          const item = v.items[0];
          return {
            label: v.title,
            className: 'px-0',
            template: () => {
              const active = pathname === AppRoutes.docs.path;
              return (
                <DocLink
                  onClick={resetSearchResult}
                  className="my-1 border-left-1"
                  style={({ isActive }) => ({
                    background: isActive || active ? 'var(--primary-color)' : 'transparent',
                    color: isActive || active ? 'var(--primary-color-text)' : 'var(--text-color)'
                  })}
                  to={AppRoutes.docs.path.concat(`/${item.slug}`)}
                >
                  {item.title}
                </DocLink>
              );
            }
          } satisfies MenuItem;
        }
        return {
          label: section.replace(/[-,_]/g, ' '),
          className: 'capitalize font-bold px-0',
          items: v.items
            .toSorted((a, b) => (a.rank ?? Number.POSITIVE_INFINITY) - (b.rank ?? Number.POSITIVE_INFINITY))
            .map(
              (el) =>
                ({
                  label: el.title,
                  className: 'capitalize',
                  template: () => {
                    return (
                      <DocLink
                        onClick={resetSearchResult}
                        className="font-medium mb-1 border-left-1"
                        style={({ isActive }) => ({
                          background: isActive ? 'var(--primary-color)' : 'transparent',
                          color: isActive ? 'var(--primary-color-text)' : 'var(--text-color)'
                        })}
                        to={AppRoutes.docs.path.concat(`/${el.slug}`)}
                      >
                        {el.title}
                      </DocLink>
                    );
                  }
                }) satisfies MenuItem
            )
        } satisfies MenuItem;
      });
  }, [pathname, manifest, resetSearchResult, externalServiceConfig]);

  return (
    <div className={classNames(className, 'w-full px-2')}>
      <CMenu model={docMenuItem} className="text-left w-full" />
    </div>
  );
};
